<div class="timeline-container">
  <!-- Header Controls -->
  <div class="timeline-header">
    <div class="flex items-center justify-between mb-4">
      <!-- Days and Zoom selectors -->
      <div class="flex items-center gap-3">
        <!-- Zoom selector -->
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ 'reservations.timeline.zoom' | translate }}:
          </span>
          <p-select [(ngModel)]="zoomLevel" [options]="zoomOptions" (ngModelChange)="onZoomChange()" optionLabel="label"
            optionValue="value" styleClass="w-35" />
        </div>

        <!-- Days selector -->
        <p-select [(ngModel)]="daysToShow" [options]="daysOptions" (ngModelChange)="onDaysChange()" optionLabel="label"
          optionValue="value" styleClass="w-35">
          <ng-template let-option pTemplate="item">
            {{ option.label | translate }}
          </ng-template>
          <ng-template let-option pTemplate="selectedItem">
            {{ option.label | translate }}
          </ng-template>
        </p-select>
      </div>
    </div>

    <!-- Legend -->
    <div class="legend">
      <!-- Estados del backend -->
      <div class="legend-item">
        <span class="legend-color" style="background-color: #fde047"></span>
        <span>{{ 'reservations.form.status.tentative' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #fbbf24"></span>
        <span>{{ 'reservations.form.status.half_confirmed' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #86efac"></span>
        <span>{{ 'reservations.form.status.booked' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #93c5fd"></span>
        <span>{{ 'reservations.form.status.checked_in' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #c4b5fd"></span>
        <span>{{ 'reservations.form.status.checked_out' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #fb923c"></span>
        <span>{{ 'reservations.form.status.no_show' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #fca5a5"></span>
        <span>{{ 'reservations.form.status.canceled' | translate }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #d1d5db"></span>
        <span>{{ 'reservations.form.status.archived' | translate }}</span>
      </div>
      <div class="legend-item">
        <i class="pi pi-heart text-red-500"></i>
        <span>{{ 'reservations.timeline.legend.treatmentDay' | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Timeline Grid -->
  <div class="timeline-wrapper">
    <div class="timeline-grid">
      <!-- Fixed bed names column -->
      <div class="beds-column">
        <div class="bed-header-cell"></div>
        @for (bed of beds; track bed) {
        <div class="bed-name-cell">
          {{ bed.bedName }}
        </div>
        }
      </div>

      <!-- Scrollable days and reservations -->
      <div class="days-scroll-container" (scroll)="onScroll($event)">
        <div class="days-container">
          <!-- Month headers -->
          <div class="month-headers">
            @for (day of days; track day; let i = $index) {
            <div class="day-header-cell" [class.month-start]="getMonthLabel(i)" [style.width.px]="getCellWidth()">
              @if (getMonthLabel(i)) {
              <div class="month-label">
                {{ getMonthLabel(i) }}
              </div>
              }
            </div>
            }
          </div>

          <!-- Day headers -->
          <div class="day-headers">
            @for (day of days; track day) {
            <div class="day-header-cell" [class.weekend]="day.isWeekend" [class.today]="day.isToday"
              [style.width.px]="getCellWidth()">
              <div class="day-number">{{ day.dayNumber }}</div>
              <div class="day-name">
                {{ day.date | date : 'EEE' }}
              </div>
            </div>
            }
          </div>

          <!-- Bed rows with reservations -->
          <div class="beds-rows">
            @for (bed of beds; track bed) {
            <div class="bed-row">
              <!-- Day cells (background grid) -->
              @for (day of days; track day; let dayIndex = $index) {
              <div class="day-cell" [class.weekend]="day.isWeekend" [class.today]="day.isToday"
                [class.has-reservation]="hasReservationOnDay(bed, dayIndex)" [style.width.px]="getCellWidth()"
                (click)="onCellClick(bed.bedId, day.date)" (keyup.enter)="onCellClick(bed.bedId, day.date)"
                (keyup.space)="onCellClick(bed.bedId, day.date)" tabindex="0" role="button"
                [attr.aria-label]="'Select ' + bed.bedId + ' on ' + (day.date | date : 'short')">
                <!-- Treatment day marker -->
                @for (reservation of bed.reservations; track reservation) {
                <div class="treatment-marker" [class.show]="shouldShowTreatmentMarker(reservation, dayIndex)">
                  <i class="pi pi-heart"></i>
                </div>
                }
              </div>
              }
              <!-- Reservation blocks and mocked plans (absolute positioned) -->
              <div class="reservations-layer">
                <!-- Real reservations -->
                @for (reservation of bed.reservations; track reservation) {
                <div class="reservation-block" [style]="getReservationStyle(reservation)" [pTooltip]="
                    reservation.patientName +
                    ' - ' +
                    ('reservations.packages.days' | translate : { days: reservation.packageDays })
                  " tooltipPosition="top" (click)="onReservationClick(reservation, $event)"
                  (keyup.enter)="onReservationClick(reservation, $event)"
                  (keyup.space)="onReservationClick(reservation, $event)" tabindex="0" role="button"
                  [attr.aria-label]="reservation.patientName + ' reservation'">
                  <div class="reservation-content">
                    <span class="patient-name">{{ reservation.patientName }}</span>
                    <span class="package-info">
                      {{ reservation.packageDays }} {{ 'reservations.packages.days' | translate }}
                    </span>
                  </div>
                </div>
                }
                <!-- Mocked plans -->
                @for (plan of bed.mockedPlans; track plan) {
                <div class="mocked-plan-block" [style]="getMockedPlanStyle(plan)" [pTooltip]="
                    plan.planName +
                    ' - ' +
                    plan.packageDays +
                    ' ' +
                    ('reservations.packages.days' | translate)
                  " tooltipPosition="top" (click)="onPlanClick(plan, $event)" tabindex="0" role="button"
                  (keyup.enter)="onPlanClick(plan, $event)" (keyup.space)="onPlanClick(plan, $event)"
                  [attr.aria-label]="plan.planName + ' - ' + plan.packageDays + ' days'">
                  <div class="plan-content">
                    <span class="plan-name">{{ plan.planName }}</span>
                    <span class="plan-badge">{{ plan.packageDays }}d</span>
                  </div>
                </div>
                }
              </div>
            </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>